<template>
  <span>
    <v-dialog max-width="1000px" persistent :value="true">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("travelService.orderFlight") }}</span>
        </v-card-title>
        <v-card-text>
          <v-form lazy-validation ref="form" v-model="formValid">
            <v-row dense>
              <v-col cols="12">
                <multiple-user-select
                  :birth-date-required="$store.getters['userProperties'].system_settings.travel_service_flight_insurance === insuranceTypeID.INSURANCE_ADD"
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6">
                <location-ajax-select
                  :label="$t('travelService.departureAirport') + '*'"
                  :placeholder="$t('travelService.enterCountry')"
                  :rules="[validatorIsRequired]"
                  :select-model.sync="contextModel.travelServiceOrderItemFlight.start_place_id"
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6">
                <location-ajax-select
                  :label="$t('travelService.arrivalAirport') + '*'"
                  :placeholder="$t('travelService.enterCountry')"
                  :rules="[validatorIsRequired]"
                  :select-model.sync="contextModel.travelServiceOrderItemFlight.end_place_id"
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6">
                <date-picker
                  :label="$t('travelService.startFlightDate') + '*'"
                  :max="contextModel.travelServiceOrderItemFlight.return_date"
                  :model.sync="contextModel.travelServiceOrderItemFlight.start_date"
                  :rules="[validatorIsRequired, validatorIsFutureDate]"
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6">
                <time-picker
                  :label="$t('travelService.startFlightTime')"
                  :model.sync="contextModel.travelServiceOrderItemFlight.start_time"
                  :placeholder="$t('travelService.any')"
                  prepend-icon=""
                />
              </v-col>
              <template v-for="segment of contextModel.travelServiceOrderItemFlight.additionalSegments">
                <!-- eslint-disable-next-line -->
                <v-layout wrap>
                  <v-col cols="12" class="mb-2 px-2">
                    <v-divider />
                  </v-col>
                  <v-col class="px-2" cols="12" sm="6">
                    <location-ajax-select
                      :label="$t('travelService.departureAirport') + '*'"
                      :placeholder="$t('travelService.enterCountry')"
                      :rules="[validatorIsRequired]"
                      :select-model.sync="segment.start_place_id"
                    />
                  </v-col>
                  <v-col class="px-2" cols="12" sm="6">
                    <location-ajax-select
                      :label="$t('travelService.arrivalAirport') + '*'"
                      :placeholder="$t('travelService.enterCountry')"
                      :rules="[validatorIsRequired]"
                      :select-model.sync="segment.end_place_id"
                    />
                  </v-col>
                  <v-col class="px-2" cols="12" sm="6">
                    <date-picker
                      :label="$t('travelService.startFlightDate') + '*'"
                      :model.sync="segment.start_date"
                      :rules="[validatorIsRequired, validatorIsFutureDate]"
                    />
                  </v-col>
                  <v-col class="px-2" cols="12" sm="6">
                    <v-row>
                      <time-picker
                        :label="$t('travelService.startFlightTime')"
                        :model.sync="segment.start_time"
                        :placeholder="$t('travelService.any')"
                        prepend-icon=""
                      />
                      <v-btn @click="removeSegment(segment)">{{ $t("travelService.removeSegment") }}</v-btn>
                    </v-row>
                  </v-col>
                </v-layout>
              </template>
              <v-col cols="12" sm="6">
                <v-row no-gutters>
                  <v-slide-x-transition>
                    <v-btn class="primary" v-show="!isReturnRoute" @click="addSegment">{{ $t("travelService.addSegment") }}</v-btn>
                  </v-slide-x-transition>
                  <v-slide-x-transition>
                    <v-checkbox
                      :label="$t('travelService.isReturnRoute')"
                      v-model="isReturnRoute"
                      v-show="contextModel.travelServiceOrderItemFlight.additionalSegments.length === 0"
                    />
                  </v-slide-x-transition>
                </v-row>
              </v-col>

              <v-col class="px-2" cols="12" sm="6">
                <v-checkbox label="Priority boarding/Fast track" v-model="contextModel.travelServiceOrderItemFlight.is_priority_boarding" />
              </v-col>
              <v-col class="px-2" cols="12" sm="6" v-if="isReturnRoute === true">
                <date-picker
                  :label="$t('travelService.returnFlightDate') + '*'"
                  :min="contextModel.travelServiceOrderItemFlight.start_date"
                  :model.sync="contextModel.travelServiceOrderItemFlight.return_date"
                  :rules="[validatorIsRequired, validatorIsFutureDate]"
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6" v-if="isReturnRoute === true">
                <time-picker
                  :label="$t('travelService.returnFlightTime')"
                  :model.sync="contextModel.travelServiceOrderItemFlight.return_time"
                  :placeholder="$t('travelService.any')"
                  prepend-icon=""
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6">
                <v-select
                  :items="availableClasses"
                  :label="$t('travelService.class') + '*'"
                  :rules="[validatorIsRequired]"
                  v-model="contextModel.travelServiceOrderItemFlight.seat_class"
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6">
                <v-text-field
                  :label="$t('travelService.preferredSeat')"
                  v-model="contextModel.travelServiceOrderItemFlight.preferred_seat"
                  :placeholder="$t('global.anyV2')"
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6">
                <v-checkbox :label="$t('travelService.registeredBaggageWillBeAssigned')" v-model="contextModel.travelServiceOrderItemFlight.is_baggage" />
              </v-col>

              <v-col class="px-2" cols="12" sm="6">
                <v-checkbox v-model="contextModel.travelServiceOrderItemFlight.is_additional_services" :label="$t('travelService.additionalServices')" />
              </v-col>
              <v-col class="px-2" cols="12" sm="6" v-if="contextModel.travelServiceOrderItemFlight.is_additional_services">
                <v-text-field
                  :label="$t('travelService.additionalServicesDetails')"
                  v-model="contextModel.travelServiceOrderItemFlight.additional_services"
                  :placeholder="$t('travelService.additionalServicesPlaceholder')"
                  :rules="[validatorIsRequired]"
                />
              </v-col>
              <v-col class="px-2" cols="12" sm="6">
                <cost-centre-select :value.sync="contextModel.cost_centre" />
              </v-col>
              <v-col class="px-2" cols="12">
                <v-text-field :label="$t('global.comment')" v-model="contextModel.comment" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click.native="cancel" color="blue darken-1" text>{{ $t("global.cancel") }}</v-btn>
          <v-btn :disabled="!formValid" @click.native="save" color="blue darken-1" text>Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import LocationAjaxSelect from "../CustomInputComponents/LocationAjaxSelect";
import DatePicker from "../CustomInputComponents/DatePicker";
import TimePicker from "../CustomInputComponents/TimePicker";
import MultipleUserSelect from "./MultipleUserSelect";
import CostCentreSelect from "./CostCentreSelect";
import moment from "moment";
import { insuranceTypeID } from "../../staticData/TravelServiceTypes";

import { allFlightClassTypes } from "../../staticData/TravelServiceTypes";
import vue from "../../main";
import { TravelServiceOrderItemFlightAdditionalSegment } from "../../models/travelOrder/travelServiceOrderItemFlight";

export default {
  name: "OrderServiceFlight",
  components: {
    CostCentreSelect,
    MultipleUserSelect,
    TimePicker,
    DatePicker,
    LocationAjaxSelect
  },
  data: () => ({
    formValid: true,
    contextModel: null,
    is_return_route: false,
    insuranceTypeID
  }),
  watch: {
    contextModel: {
      deep: true,
      handler() {
        if (JSON.stringify(this.contextModel) !== JSON.stringify(this.editedTravelServiceOrderItem)) {
          this.updateVuexState();
        }
      }
    },
    editedTravelServiceOrderItem: {
      deep: true,
      handler(val) {
        this.contextModel = JSON.parse(JSON.stringify(val));
      }
    }
  },
  computed: {
    isReturnRoute: {
      get() {
        return this.is_return_route || this.contextModel.travelServiceOrderItemFlight.return_date !== null;
      },
      set(val) {
        if (val) {
          this.is_return_route = true;
        } else {
          this.is_return_route = false;
          this.contextModel.travelServiceOrderItemFlight.return_date = null;
          this.contextModel.travelServiceOrderItemFlight.return_time = null;
        }
      }
    },
    availableClasses: allFlightClassTypes,
    editedTravelServiceOrderItem() {
      return this.$store.state.TravelService.editedTravelServiceItem;
    }
  },
  methods: {
    addSegment() {
      this.isReturnRoute = false;
      this.contextModel.travelServiceOrderItemFlight.additionalSegments.push(new TravelServiceOrderItemFlightAdditionalSegment());
    },
    removeSegment(item) {
      this.contextModel.travelServiceOrderItemFlight.additionalSegments.splice(
        this.contextModel.travelServiceOrderItemFlight.additionalSegments.indexOf(item),
        1
      );
    },
    validatorIsRequired(v) {
      return (v != null && v.length > 0) || this.$t("global.error.required");
    },
    validatorIsFutureDate(v) {
      return (
        moment(v)
          .endOf("day")
          .isAfter(moment()) || vue.$t("global.error.dateLaterThanNow")
      );
    },
    updateVuexState() {
      this.$store.commit("TravelService/setEditedTravelServiceItem", this.contextModel);
    },
    cancel() {
      this.$store.commit("TravelService/setEditedTravelServiceItem", null);
      this.$store.commit("TravelService/setOldTravelServiceItem", null);
      this.$refs.form.resetValidation();
    },
    save() {
      if (this.$refs.form.validate()) {
        this.$store.dispatch("TravelService/saveEditedServiceItem");
      }
    }
  },
  created() {
    //   this.contextModel.is_insurance = parseInt(this.$store.getters["userProperties"].system_settings.travel_service_flight_insurance) === 1;
    this.contextModel = JSON.parse(JSON.stringify(this.editedTravelServiceOrderItem));
  }
};
</script>

<style scoped></style>
